import { type IRowNode } from "ag-grid-community"
import { type AgGridReact } from "ag-grid-react"
import React, { useCallback, useEffect, useRef } from "react"
import { useEventCallback } from "usehooks-ts"

import {
  useHighlightSubscription,
  useReadHighlightedIds,
  useRegisterHighlightStepper,
  useSelectionMirroredHighlight,
  useSubscribeHighlightedIds,
  type HighlightStepper,
} from "~/components/design-system/pages"
import {
  useSelectedVariantHandlers,
  useSelectedVariantState,
} from "~/context/SelectedVariantsProvider"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { getDisplayedLeafNodes, leafRowIds, stepDisplayedLeafNode } from "~/utils/ag-utils"
import { filterDefined } from "~/utils/tsUtils"

/** Styled in globals.css. */
export const HIGHLIGHT_ROW_CLASS = "ag-row-inspector-highlight"

const EMPTY_HIGHLIGHT: ReadonlySet<string> = new Set()

const sameHighlight = (a: ReadonlySet<string>, b: ReadonlySet<string>) => {
  if (a === b) return true
  if (a.size !== b.size) return false
  for (const id of a) if (!b.has(id)) return false
  return true
}

// A multi-row highlight has no cursor, so stepping restarts from the top.
const stepperCursor = (ids: ReadonlySet<string>) => (ids.size === 1 ? ([...ids][0] ?? null) : null)

export function useRowHighlight({
  gridRef,
  highlightOnRowClick,
  onHighlightedVariantsChange,
  isGridReady,
}: {
  gridRef: React.RefObject<AgGridReact<IVariantSummary> | null>
  highlightOnRowClick: boolean
  onHighlightedVariantsChange: ((variantIds: string[]) => void) | undefined
  isGridReady: boolean
}) {
  const highlightedIdsRef = useRef<ReadonlySet<string>>(EMPTY_HIGHLIGHT)
  const publishedIdsRef = useRef<ReadonlySet<string>>(EMPTY_HIGHLIGHT)
  // In-cell peek buttons re-render from this. Stable, so it stays out of the column memo.
  const highlightSubscription = useHighlightSubscription()
  const onHighlightedVariantsChangeRef = useRef(onHighlightedVariantsChange)
  useEffect(() => {
    onHighlightedVariantsChangeRef.current = onHighlightedVariantsChange
  })

  const { registerStepper, unregisterStepper, setPosition } = useRegisterHighlightStepper()

  // A snapshot for the inspector, which can't re-render off a grid query.
  const publishPosition = useCallback(() => {
    if (!highlightOnRowClick) return
    const api = gridRef.current?.api
    if (!api || api.isDestroyed?.()) return
    const currentIds = highlightedIdsRef.current
    const nodes = getDisplayedLeafNodes(api)
    // -1 hides the inspector's carets for a multi-row highlight.
    const currentId = currentIds.size === 1 ? [...currentIds][0] : null
    setPosition({
      index: currentId == null ? -1 : nodes.findIndex((n) => n.id === currentId),
      total: nodes.length,
    })
  }, [highlightOnRowClick, gridRef, setPosition])

  // `variantIds` overrides what is published: ticked variants a search removed have no row.
  const applyHighlight = useCallback(
    (next: string | readonly string[] | null, variantIds: readonly string[] | null) => {
      const api = gridRef.current?.api
      const previousIds = highlightedIdsRef.current
      const nextIds: ReadonlySet<string> =
        next == null ? EMPTY_HIGHLIGHT : new Set(typeof next === "string" ? [next] : next)
      // Under grouping one variant spans several rows.
      const published: ReadonlySet<string> = new Set(
        variantIds ?? filterDefined([...nextIds].map((id) => api?.getRowNode(id)?.data?.variantId)),
      )
      // By membership: every call builds new sets.
      const rowsChanged = !sameHighlight(previousIds, nextIds)
      if (!rowsChanged && sameHighlight(publishedIdsRef.current, published)) return
      highlightedIdsRef.current = nextIds
      publishedIdsRef.current = published
      if (rowsChanged && api && !api.isDestroyed?.()) {
        // A class toggle, not `redrawRows`/`refreshCells`: the highlight moves on mousedown, and
        // re-rendering the row would destroy the element the click started on.
        const changed: [string, boolean][] = []
        for (const id of previousIds) if (!nextIds.has(id)) changed.push([id, false])
        for (const id of nextIds) if (!previousIds.has(id)) changed.push([id, true])
        for (const [id, on] of changed) {
          const rowIndex = api.getRowNode(id)?.rowIndex
          if (rowIndex == null) continue
          // querySelectorAll, not one element: ag-grid renders a row once per pinned section.
          document
            .querySelectorAll(`.ag-row[row-index="${rowIndex}"]`)
            .forEach((el) => el.classList.toggle(HIGHLIGHT_ROW_CLASS, on))
        }
      }
      highlightSubscription.set(nextIds)
      onHighlightedVariantsChangeRef.current?.([...published])
      publishPosition()
    },
    [gridRef, publishPosition, highlightSubscription],
  )

  // Every highlight move goes through an entry point named for its source.
  const { getSelectedVariantIds, clearSelectedVariants } = useSelectedVariantHandlers()
  const {
    fromRow: highlightRows,
    fromSelection,
    fromStore,
  } = useSelectionMirroredHighlight<string | readonly string[]>({
    apply: applyHighlight,
    getSelectedIds: getSelectedVariantIds,
    getSelectedRows: () => {
      const api = gridRef.current?.api
      return api && !api.isDestroyed?.() ? leafRowIds(api.getSelectedNodes()) : []
    },
    clearSelection: clearSelectedVariants,
  })

  // Shared by the prev/next buttons and the keyboard shortcuts.
  useEffect(() => {
    if (!highlightOnRowClick) return
    const stepper: HighlightStepper = {
      step: (delta) => {
        const api = gridRef.current?.api
        if (!api || api.isDestroyed?.()) return null
        const node = stepDisplayedLeafNode(
          api,
          stepperCursor(highlightedIdsRef.current),
          delta,
          (n) => n.id ?? null,
        )
        if (!node?.id || node.rowIndex == null) return null
        highlightRows(node.id)
        api.ensureIndexVisible(node.rowIndex)
        // Else the arrow keys resume from the old focus.
        const column = api.getFocusedCell()?.column ?? api.getAllDisplayedColumns()[0]
        if (column) api.setFocusedCell(node.rowIndex, column)
        return node.id
      },
      clear: () => {
        highlightRows(null)
        // Else the next arrow key resumes from the cleared row.
        const api = gridRef.current?.api
        if (api && !api.isDestroyed?.()) api.clearFocusedCell()
      },
    }
    registerStepper(stepper)
    return () => unregisterStepper(stepper)
  }, [highlightOnRowClick, gridRef, highlightRows, registerStepper, unregisterStepper])

  // Gated on isGridReady for the same reason as the restore effect below.
  useEffect(() => {
    if (!highlightOnRowClick || !isGridReady) return
    const api = gridRef.current?.api
    if (!api || api.isDestroyed?.()) return
    const republish = () => publishPosition()
    api.addEventListener("modelUpdated", republish)
    api.addEventListener("sortChanged", republish)
    return () => {
      if (api.isDestroyed?.()) return
      api.removeEventListener("modelUpdated", republish)
      api.removeEventListener("sortChanged", republish)
    }
  }, [highlightOnRowClick, isGridReady, gridRef, publishPosition])

  const readHighlightedIds = useReadHighlightedIds()
  const subscribeHighlightedIds = useSubscribeHighlightedIds()

  // Paints through `applyHighlight`: a programmatic `setFocusedCell` raises no `cellFocused`.
  // Stored ids are variants, so under grouping one lights a row per group.
  const scrolledToHighlightRef = useRef(false)
  const restoreHighlight = useEventCallback(() => {
    if (!highlightOnRowClick) return
    const ids = readHighlightedIds()
    const api = gridRef.current?.api
    if (!api || api.isDestroyed?.()) return
    if (ids.length === 0) {
      scrolledToHighlightRef.current = false
      return
    }
    const wanted = new Set(ids)
    const matches: IRowNode<IVariantSummary>[] = []
    api.forEachNodeAfterFilterAndSort((node) => {
      if (node.group || node.footer) return
      if (node.data && node.id != null && wanted.has(node.data.variantId)) matches.push(node)
    })
    const rowIds = filterDefined(matches.map((node) => node.id))
    if (rowIds.length === 0) return
    // The ticks' own echo: not adopted, nor scrolled to (a select-all would jump to row 0).
    if (!fromStore(ids, rowIds)) return
    if (!scrolledToHighlightRef.current) {
      const firstIndex = matches[0]?.rowIndex
      if (firstIndex != null) api.ensureIndexVisible(firstIndex)
      scrolledToHighlightRef.current = true
    }
  })

  // Gated on isGridReady: `gridRef.current` is null on mount and the effect would never retry.
  useEffect(() => {
    if (!highlightOnRowClick || !isGridReady) return
    const api = gridRef.current?.api
    if (!api || api.isDestroyed?.()) return
    restoreHighlight()
    api.addEventListener("modelUpdated", restoreHighlight)
    return () => {
      if (!api.isDestroyed?.()) api.removeEventListener("modelUpdated", restoreHighlight)
    }
  }, [highlightOnRowClick, isGridReady, gridRef, restoreHighlight])

  // An outside highlight raises no grid event. Deferred: `applyHighlight` writes the store
  // mid-call, so a synchronous restore would run inside it.
  useEffect(() => {
    if (!highlightOnRowClick || !isGridReady) return
    return subscribeHighlightedIds(() => queueMicrotask(restoreHighlight))
  }, [highlightOnRowClick, isGridReady, subscribeHighlightedIds, restoreHighlight])

  return { highlightedIdsRef, highlightSubscription, highlightRows, fromSelection }
}

/**
 * Follows the provider's count, not ag-grid's `selectionChanged`: the provider also drops ticks a
 * search took out of rowData, with no grid event. A child, so a tick re-renders only this.
 */
export const SelectionHighlightSync = ({ fromSelection }: { fromSelection: () => void }) => {
  const { selectedVariantCount } = useSelectedVariantState()
  useEffect(() => {
    fromSelection()
  }, [selectedVariantCount, fromSelection])
  return null
}
